package com.tovtalk.ui.screen.settings

import androidx.compose.animation.animateColorAsState
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Language
import androidx.compose.material.icons.filled.Block
import androidx.compose.material.icons.filled.ChatBubble
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.Devices
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material.icons.filled.VerifiedUser
import androidx.compose.material.icons.filled.PhoneAndroid
import androidx.compose.material.icons.filled.PhoneIphone
import androidx.compose.material.icons.filled.Laptop
import androidx.compose.material.icons.filled.Report
import androidx.compose.material.icons.filled.Tv
import androidx.compose.material.icons.filled.VideoLibrary
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.Category
import com.tovtalk.domain.entity.KosherLevel
import com.tovtalk.domain.entity.resolvedName
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.ui.adaptive.adaptive
import com.tovtalk.ui.adaptive.isMobile
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.config.isHadranDevice
import com.tovtalk.utils.language.LanguageCodes
import com.tovtalk.utils.strings.StringKeys
import com.tovtalk.utils.strings.StringProvider
import com.tovtalk.data.local.TokenManager
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.ui.platform.LocalUriHandler
import kotlinx.coroutines.launch

/** Represents which detail screen is selected in the split view. */
private enum class SettingsDetail {
    NONE,
    NOTIFICATIONS,
    BLOCKED_USERS,
    CONTENT_UPLOAD,
    SESSIONS,
    ABOUT,
    PRIVACY,
    TERMS,
}

@Composable
fun SettingsScreen(
    onNavigateBack: () -> Unit,
    onNavigateToNotificationSettings: () -> Unit,
    onNavigateToBlockedUsers: () -> Unit = {},
    onNavigateToContentUploadManagement: () -> Unit = {},
    onNavigateToSessions: () -> Unit = {},
    onNavigateToPhoneVerification: () -> Unit = {},
    onNavigateToAbout: () -> Unit,
    onNavigateToPrivacy: () -> Unit,
    onNavigateToTerms: () -> Unit = {},
    onNavigateToLogin: (() -> Unit)? = null,
    onLogout: () -> Unit = {},
    onLanguageApplied: (String) -> Unit = {},
    settingsRepository: SettingsRepository = koinInject(),
    userRepository: UserRepository = koinInject(),
    appRepository: AppRepository = koinInject()
) {
    val mobile = isMobile()
    if (mobile) {
        SettingsScreenContent(
            onNavigateBack = onNavigateBack,
            onNavigateToNotificationSettings = onNavigateToNotificationSettings,
            onNavigateToBlockedUsers = onNavigateToBlockedUsers,
            onNavigateToContentUploadManagement = onNavigateToContentUploadManagement,
            onNavigateToSessions = onNavigateToSessions,
            onNavigateToPhoneVerification = onNavigateToPhoneVerification,
            onNavigateToAbout = onNavigateToAbout,
            onNavigateToPrivacy = onNavigateToPrivacy,
            onNavigateToTerms = onNavigateToTerms,
            onNavigateToLogin = onNavigateToLogin,
            onLogout = onLogout,
            onLanguageApplied = onLanguageApplied,
            settingsRepository = settingsRepository,
            userRepository = userRepository,
            appRepository = appRepository,
            showDescriptions = false,
        )
    } else {
        SettingsSplitView(
            onNavigateBack = onNavigateBack,
            onNavigateToPhoneVerification = onNavigateToPhoneVerification,
            onNavigateToLogin = onNavigateToLogin,
            onLogout = onLogout,
            onLanguageApplied = onLanguageApplied,
            settingsRepository = settingsRepository,
            userRepository = userRepository,
            appRepository = appRepository,
        )
    }
}

@Composable
private fun SettingsSplitView(
    onNavigateBack: () -> Unit,
    onNavigateToPhoneVerification: () -> Unit,
    onNavigateToLogin: (() -> Unit)?,
    onLogout: () -> Unit,
    onLanguageApplied: (String) -> Unit,
    settingsRepository: SettingsRepository,
    userRepository: UserRepository,
    appRepository: AppRepository,
) {
    var selectedDetail by remember { mutableStateOf(SettingsDetail.NONE) }

    val listWidth = adaptive(
        compact = 220.dp, mediumCompact = 220.dp, medium = 220.dp,
        expanded = 260.dp, large = 280.dp, extraLarge = 300.dp
    )
    val contentMaxWidth = adaptive(
        compact = Dp.Unspecified, mediumCompact = Dp.Unspecified, medium = Dp.Unspecified,
        expanded = 420.dp, large = 420.dp, extraLarge = 420.dp
    )

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("settings_title"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            // Left panel: settings list
            Box(
                modifier = Modifier
                    .width(listWidth)
                    .fillMaxHeight()
            ) {
                SettingsListPanel(
                    selectedDetail = selectedDetail,
                    onSelectDetail = { selectedDetail = it },
                    onNavigateToPhoneVerification = onNavigateToPhoneVerification,
                    onNavigateToLogin = onNavigateToLogin,
                    onLogout = onLogout,
                    onLanguageApplied = onLanguageApplied,
                    settingsRepository = settingsRepository,
                    userRepository = userRepository,
                    appRepository = appRepository,
                )
            }

            VerticalDivider(color = TovTalkTheme.colors.border)

            // Right panel: detail content
            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
            ) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.TopCenter
                ) {
                    Box(
                        modifier = if (contentMaxWidth != Dp.Unspecified)
                            Modifier.widthIn(max = contentMaxWidth).fillMaxHeight()
                        else
                            Modifier.fillMaxSize()
                    ) {
                        when (selectedDetail) {
                            SettingsDetail.NOTIFICATIONS -> NotificationSettingsScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                                onNavigateToLogin = onNavigateToLogin,
                            )
                            SettingsDetail.BLOCKED_USERS -> BlockedUsersScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                            )
                            SettingsDetail.CONTENT_UPLOAD -> ContentUploadManagementScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                            )
                            SettingsDetail.SESSIONS -> SessionsScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                            )
                            SettingsDetail.ABOUT -> AboutScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                            )
                            SettingsDetail.PRIVACY -> PrivacyScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                            )
                            SettingsDetail.TERMS -> TermsScreen(
                                onNavigateBack = { selectedDetail = SettingsDetail.NONE },
                            )
                            SettingsDetail.NONE -> Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = StringProvider.get("settings_select_item"),
                                    style = TovTalkTheme.typography.bodyLarge,
                                    color = TovTalkTheme.colors.textSecondary
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SettingsListPanel(
    selectedDetail: SettingsDetail,
    onSelectDetail: (SettingsDetail) -> Unit,
    onNavigateToPhoneVerification: () -> Unit,
    onNavigateToLogin: (() -> Unit)?,
    onLogout: () -> Unit,
    onLanguageApplied: (String) -> Unit,
    settingsRepository: SettingsRepository,
    userRepository: UserRepository,
    appRepository: AppRepository,
) {
    SettingsScreenContent(
        onNavigateBack = {},
        onNavigateToNotificationSettings = { onSelectDetail(SettingsDetail.NOTIFICATIONS) },
        onNavigateToBlockedUsers = { onSelectDetail(SettingsDetail.BLOCKED_USERS) },
        onNavigateToContentUploadManagement = { onSelectDetail(SettingsDetail.CONTENT_UPLOAD) },
        onNavigateToSessions = { onSelectDetail(SettingsDetail.SESSIONS) },
        onNavigateToPhoneVerification = onNavigateToPhoneVerification,
        onNavigateToAbout = { onSelectDetail(SettingsDetail.ABOUT) },
        onNavigateToPrivacy = { onSelectDetail(SettingsDetail.PRIVACY) },
        onNavigateToTerms = { onSelectDetail(SettingsDetail.TERMS) },
        onNavigateToLogin = onNavigateToLogin,
        onLogout = onLogout,
        onLanguageApplied = onLanguageApplied,
        settingsRepository = settingsRepository,
        userRepository = userRepository,
        appRepository = appRepository,
        showDescriptions = true,
        selectedDetail = selectedDetail,
        hideSelfTopBar = true,
    )
}

@Composable
private fun SettingsScreenContent(
    onNavigateBack: () -> Unit,
    onNavigateToNotificationSettings: () -> Unit,
    onNavigateToBlockedUsers: () -> Unit,
    onNavigateToContentUploadManagement: () -> Unit,
    onNavigateToSessions: () -> Unit,
    onNavigateToPhoneVerification: () -> Unit,
    onNavigateToAbout: () -> Unit,
    onNavigateToPrivacy: () -> Unit,
    onNavigateToTerms: () -> Unit,
    onNavigateToLogin: (() -> Unit)?,
    onLogout: () -> Unit,
    onLanguageApplied: (String) -> Unit,
    settingsRepository: SettingsRepository,
    userRepository: UserRepository,
    appRepository: AppRepository,
    showDescriptions: Boolean = false,
    selectedDetail: SettingsDetail = SettingsDetail.NONE,
    hideSelfTopBar: Boolean = false,
) {
    val authSheetController: AuthSheetController = koinInject()
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current
    val authState by TokenManager.authStateFlow.collectAsState()
    val isGuestMode = authState.isGuestMode
    var currentLanguage by remember { mutableStateOf(LanguageCodes.ENGLISH) }
    var currentKashrutLevel by remember { mutableStateOf<Int?>(null) }
    var kashrutLabel by remember { mutableStateOf("") }
    var kosherLevels by remember { mutableStateOf<List<KosherLevel>>(emptyList()) }
    var categories by remember { mutableStateOf<List<Category>>(emptyList()) }
    var selectedConsumptionCategoryIds by remember { mutableStateOf<Set<Int>>(emptySet()) }
    var showCategoriesDialog by remember { mutableStateOf(false) }
    var currentUserIsReadOnly by remember { mutableStateOf(false) }
    var receiveMessages by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        currentLanguage = settingsRepository.getAppLanguage()

        // Load kosher levels
        val isHadran = isHadranDevice()
        when (val result = appRepository.getKosherLevels(hadranOnly = isHadran)) {
            is com.tovtalk.domain.entity.DomainResult.Success -> {
                kosherLevels = result.data
            }
            else -> {
                // Fallback to all levels if API fails
                kosherLevels = emptyList()
            }
        }

        // Load current user (kashrut + devices only for read-only users)
        when (val r = userRepository.getCurrentUser()) {
            is com.tovtalk.domain.entity.DomainResult.Success -> {
                currentUserIsReadOnly = r.data.isReadOnly
                currentKashrutLevel = r.data.kashrutLevel
                receiveMessages = r.data.receiveMessage
                selectedConsumptionCategoryIds = (r.data.consumptionCategoryIds ?: emptyList()).toSet()
                kashrutLabel = kosherLevels.find { it.id == r.data.kashrutLevel }?.resolvedName(currentLanguage) ?: ""
            }
            else -> {}
        }
        when (val catResult = appRepository.getCategories()) {
            is com.tovtalk.domain.entity.DomainResult.Success -> categories = catResult.data
            else -> {}
        }
    }
    var showLanguageDialog by remember { mutableStateOf(false) }
    var showKashrutDialog by remember { mutableStateOf(false) }
    var showLogoutDialog by remember { mutableStateOf(false) }
    var isLoggingOut by remember { mutableStateOf(false) }
    var showDeleteAccountDialog by remember { mutableStateOf(false) }
    var isDeletingAccount by remember { mutableStateOf(false) }
    var deleteAccountError by remember { mutableStateOf<String?>(null) }
    var kashrutErrorMessage by remember { mutableStateOf<String?>(null) }
    var showKashrutErrorDialog by remember { mutableStateOf(false) }

    if (showLogoutDialog) {
        AlertDialog(
            onDismissRequest = { if (!isLoggingOut) showLogoutDialog = false },
            title = { Text(StringProvider.get("profile_logout"), style = TovTalkTheme.typography.titleMedium) },
            text = { Text(StringProvider.get("profile_logout_confirm"), style = TovTalkTheme.typography.bodyMedium) },
            confirmButton = {
                TextButton(
                    onClick = {
                        isLoggingOut = true
                        scope.launch {
                            userRepository.logout()
                            showLogoutDialog = false
                            isLoggingOut = false
                            onLogout()
                        }
                    },
                    enabled = !isLoggingOut,
                    colors = ButtonDefaults.textButtonColors(contentColor = TovTalkTheme.colors.error)
                ) {
                    if (isLoggingOut) {
                        CircularProgressIndicator(modifier = Modifier.size(TovTalkTheme.sizes.iconXs), strokeWidth = TovTalkTheme.sizes.borderWidth)
                    } else {
                        Text(StringProvider.get("profile_logout"))
                    }
                }
            },
            dismissButton = {
                TextButton(onClick = { showLogoutDialog = false }, enabled = !isLoggingOut) {
                    Text(StringProvider.get("common_cancel"))
                }
            },
            containerColor = TovTalkTheme.colors.surfaceElevated
        )
    }

    if (showDeleteAccountDialog) {
        AlertDialog(
            onDismissRequest = { if (!isDeletingAccount) { showDeleteAccountDialog = false; deleteAccountError = null } },
            title = { Text(StringProvider.get("settings_delete_account"), style = TovTalkTheme.typography.titleMedium) },
            text = {
                Column {
                    Text(StringProvider.get("settings_delete_account_confirm"), style = TovTalkTheme.typography.bodyMedium)
                    deleteAccountError?.let { err ->
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                        Text(err, style = TovTalkTheme.typography.bodySmall, color = TovTalkTheme.colors.error)
                    }
                }
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        isDeletingAccount = true
                        deleteAccountError = null
                        scope.launch {
                            when (val result = userRepository.deleteAccount()) {
                                is com.tovtalk.domain.entity.DomainResult.Success -> {
                                    showDeleteAccountDialog = false
                                    isDeletingAccount = false
                                    onLogout()
                                }
                                is com.tovtalk.domain.entity.DomainResult.Error -> {
                                    deleteAccountError = result.message
                                    isDeletingAccount = false
                                }
                                is com.tovtalk.domain.entity.DomainResult.Loading -> {}
                            }
                        }
                    },
                    enabled = !isDeletingAccount,
                    colors = ButtonDefaults.textButtonColors(contentColor = TovTalkTheme.colors.error)
                ) {
                    if (isDeletingAccount) {
                        CircularProgressIndicator(modifier = Modifier.size(TovTalkTheme.sizes.iconXs), strokeWidth = TovTalkTheme.sizes.borderWidth)
                    } else {
                        Text(StringProvider.get("settings_delete_account"))
                    }
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteAccountDialog = false; deleteAccountError = null }, enabled = !isDeletingAccount) {
                    Text(StringProvider.get("common_cancel"))
                }
            },
            containerColor = TovTalkTheme.colors.surfaceElevated
        )
    }

    if (currentUserIsReadOnly && showKashrutDialog) {
        KashrutSelectionDialog(
            currentLevel = currentKashrutLevel ?: (kosherLevels.minByOrNull { it.order }?.id ?: 1),
            availableLevels = kosherLevels,
            lang = currentLanguage,
            onLevelSelected = { level ->
                scope.launch {
                    val selectedLevel = kosherLevels.find { it.id == level }
                    val requiresVerification = selectedLevel?.requiresGenderVerification == true
                    when (val result = userRepository.updateKashrutLevel(level)) {
                        is com.tovtalk.domain.entity.DomainResult.Success -> {
                            currentKashrutLevel = level
                            // Get label from loaded kosher levels (from API)
                            kashrutLabel = kosherLevels.find { it.id == level }?.resolvedName(currentLanguage) ?: ""

                            // Navigate to verification if required
                            if (requiresVerification) {
                                onNavigateToPhoneVerification()
                            }
                        }
                        is com.tovtalk.domain.entity.DomainResult.Error -> {
                            // Show error message
                            kashrutErrorMessage = result.message
                            showKashrutErrorDialog = true
                        }
                        is com.tovtalk.domain.entity.DomainResult.Loading -> {
                            // Loading state
                        }
                    }
                }
                showKashrutDialog = false
            },
            onDismiss = { showKashrutDialog = false }
        )
    }

    if (showCategoriesDialog && categories.isNotEmpty()) {
        var dialogSelectedIds by remember(showCategoriesDialog) { mutableStateOf(selectedConsumptionCategoryIds) }
        AlertDialog(
            onDismissRequest = { showCategoriesDialog = false },
            title = { Text(StringProvider.get("settings_favorite_categories"), style = TovTalkTheme.typography.titleMedium) },
            text = {
                Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                    categories.sortedBy { it.sortOrder }.forEach { category ->
                        val selected = category.id in dialogSelectedIds
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(TovTalkTheme.shapes.small)
                                .background(if (selected) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft) else TovTalkTheme.colors.surfaceOverlay)
                                .clickable {
                                    dialogSelectedIds = if (selected) dialogSelectedIds - category.id else dialogSelectedIds + category.id
                                }
                                .padding(TovTalkTheme.spacing.md),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Checkbox(
                                checked = selected,
                                onCheckedChange = {
                                    dialogSelectedIds = if (selected) dialogSelectedIds - category.id else dialogSelectedIds + category.id
                                },
                                colors = CheckboxDefaults.colors(checkedColor = TovTalkTheme.colors.brand)
                            )
                            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                            Text(
                                text = category.name,
                                style = TovTalkTheme.typography.bodyLarge,
                                color = TovTalkTheme.colors.textPrimary
                            )
                        }
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    scope.launch {
                        selectedConsumptionCategoryIds = dialogSelectedIds
                        userRepository.updateProfile(mapOf("consumption_category_ids" to dialogSelectedIds.toList()))
                        showCategoriesDialog = false
                    }
                }) {
                    Text(StringProvider.get("common_save"))
                }
            },
            dismissButton = {
                TextButton(onClick = { showCategoriesDialog = false }) {
                    Text(StringProvider.get("common_cancel"))
                }
            },
            containerColor = TovTalkTheme.colors.surfaceElevated
        )
    }

    if (showKashrutErrorDialog && kashrutErrorMessage != null) {
        AlertDialog(
            onDismissRequest = { showKashrutErrorDialog = false },
            title = { Text(StringProvider.get("common_error"), style = TovTalkTheme.typography.titleMedium) },
            text = {
                Text(
                    text = kashrutErrorMessage ?: "",
                    style = TovTalkTheme.typography.bodyMedium
                )
            },
            confirmButton = {
                TextButton(onClick = {
                    showKashrutErrorDialog = false
                    // If error is about verification required, navigate to verification
                    if (kashrutErrorMessage?.contains("verification", ignoreCase = true) == true ||
                        kashrutErrorMessage?.contains("אימות") == true) {
                        onNavigateToPhoneVerification()
                    }
                }) {
                    Text(if (kashrutErrorMessage?.contains("verification", ignoreCase = true) == true ||
                        kashrutErrorMessage?.contains("אימות") == true) StringProvider.get("settings_go_to_verification") else StringProvider.get("common_ok"))
                }
            },
            dismissButton = if (kashrutErrorMessage?.contains("verification", ignoreCase = true) == true ||
                kashrutErrorMessage?.contains("אימות") == true) {
                {
                    TextButton(onClick = { showKashrutErrorDialog = false }) {
                        Text(StringProvider.get("common_cancel"))
                    }
                }
            } else null,
            containerColor = TovTalkTheme.colors.surfaceElevated
        )
    }

    if (showLanguageDialog) {
        LanguageSelectionDialog(
            currentLanguage = currentLanguage,
            onLanguageSelected = { newLanguage ->
                scope.launch {
                    settingsRepository.setAppLanguage(newLanguage)
                    settingsRepository.loadTranslations(newLanguage)
                    currentLanguage = newLanguage
                    onLanguageApplied(newLanguage)
                }
                showLanguageDialog = false
            },
            onDismiss = { showLanguageDialog = false }
        )
    }

    // Build the LazyColumn list content as a lambda so it can be used with or without scaffold
    val listContent: @Composable (topPadding: PaddingValues) -> Unit = { paddingValues ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            // ── Section 1: Account ──
            item { SettingsSectionHeader(title = StringProvider.get("settings_account")) }
            item {
                FlatSettingsItem(
                    icon = Icons.Default.Language,
                    title = StringProvider.get("settings_language"),
                    subtitle = if (currentLanguage == LanguageCodes.HEBREW) StringProvider.get("language_hebrew") else StringProvider.get("language_english"),
                    onClick = { showLanguageDialog = true },
                    isSelected = false,
                    showDescription = showDescriptions,
                )
            }
            if (!isGuestMode && currentUserIsReadOnly) {
                item { SettingsDivider() }
                item {
                    FlatSettingsItem(
                        icon = Icons.Default.Devices,
                        title = StringProvider.get("settings_devices"),
                        subtitle = StringProvider.get("settings_devices_desc"),
                        onClick = onNavigateToSessions,
                        isSelected = selectedDetail == SettingsDetail.SESSIONS,
                        showDescription = showDescriptions,
                    )
                }
            }
            item { SettingsDivider() }
            item {
                FlatSettingsItem(
                    icon = Icons.Default.Lock,
                    title = StringProvider.get("settings_privacy"),
                    subtitle = null,
                    onClick = onNavigateToPrivacy,
                    isSelected = selectedDetail == SettingsDetail.PRIVACY,
                    showDescription = showDescriptions,
                )
            }

            // ── Section 2: Content & Preferences ──
            if (!isGuestMode && !currentUserIsReadOnly) {
                item { SettingsSectionHeader(title = StringProvider.get("settings_content_preferences")) }
                item {
                    FlatSettingsItem(
                        icon = Icons.Default.VideoLibrary,
                        title = StringProvider.get("settings_content_upload_management"),
                        subtitle = null,
                        onClick = onNavigateToContentUploadManagement,
                        isSelected = selectedDetail == SettingsDetail.CONTENT_UPLOAD,
                        showDescription = showDescriptions,
                    )
                }
            }
            if (!isGuestMode && currentUserIsReadOnly && categories.isNotEmpty()) {
                item { SettingsDivider() }
                item {
                    FlatSettingsItem(
                        icon = Icons.Default.FilterList,
                        title = StringProvider.get("settings_favorite_categories"),
                        subtitle = categories.filter { it.id in selectedConsumptionCategoryIds }.joinToString(", ") { it.name }.ifBlank { StringProvider.get("categories_interest_subtitle") },
                        onClick = { showCategoriesDialog = true },
                        isSelected = false,
                        showDescription = showDescriptions,
                    )
                }
            }
            if (!isGuestMode && currentUserIsReadOnly) {
                item { SettingsDivider() }
                item {
                    FlatSettingsItem(
                        icon = Icons.Default.FilterList,
                        title = StringProvider.get("settings_kashrut"),
                        subtitle = kashrutLabel.ifBlank { StringProvider.get("kashrut_level_1") },
                        onClick = { showKashrutDialog = true },
                        isSelected = false,
                        showDescription = showDescriptions,
                    )
                }
            }

            // ── Section 3: Social ──
            item { SettingsSectionHeader(title = StringProvider.get("settings_social")) }
            item {
                FlatSettingsItem(
                    icon = Icons.Default.Notifications,
                    title = StringProvider.get("settings_notifications"),
                    subtitle = if (isGuestMode) StringProvider.get("profile_guest_subtitle") else StringProvider.get("settings_notifications_desc"),
                    onClick = if (isGuestMode) { { authSheetController.open() } } else { onNavigateToNotificationSettings },
                    isSelected = selectedDetail == SettingsDetail.NOTIFICATIONS,
                    showDescription = showDescriptions,
                )
            }
            if (!isGuestMode) {
                item { SettingsDivider() }
                item {
                    FlatSettingsToggleItem(
                        icon = Icons.Default.ChatBubble,
                        title = StringProvider.get(StringKeys.SETTINGS_RECEIVE_MESSAGES),
                        subtitle = StringProvider.get(StringKeys.SETTINGS_RECEIVE_MESSAGES_DESC),
                        checked = receiveMessages,
                        onCheckedChange = { value ->
                            receiveMessages = value
                            scope.launch {
                                when (val r = userRepository.updateUserSettings(receiveMessage = value)) {
                                    is com.tovtalk.domain.entity.DomainResult.Success -> { }
                                    else -> receiveMessages = !value
                                }
                            }
                        }
                    )
                }
                item { SettingsDivider() }
                item {
                    FlatSettingsItem(
                        icon = Icons.Default.Block,
                        title = StringProvider.get("settings_blocked_users"),
                        subtitle = StringProvider.get("settings_blocked_users_desc"),
                        onClick = onNavigateToBlockedUsers,
                        isSelected = selectedDetail == SettingsDetail.BLOCKED_USERS,
                        showDescription = showDescriptions,
                    )
                }
            }

            // ── Section 4: Support & Info ──
            item { SettingsSectionHeader(title = StringProvider.get("settings_support")) }
            item {
                FlatSettingsItem(
                    icon = Icons.Default.Info,
                    title = StringProvider.get("settings_about"),
                    subtitle = null,
                    onClick = onNavigateToAbout,
                    isSelected = selectedDetail == SettingsDetail.ABOUT,
                    showDescription = showDescriptions,
                )
            }
            item { SettingsDivider() }
            item {
                FlatSettingsItem(
                    icon = Icons.Default.Description,
                    title = StringProvider.get("settings_terms"),
                    subtitle = null,
                    onClick = onNavigateToTerms,
                    isSelected = selectedDetail == SettingsDetail.TERMS,
                    showDescription = showDescriptions,
                )
            }
            item { SettingsDivider() }
            item {
                FlatSettingsItem(
                    icon = Icons.Default.Report,
                    title = StringProvider.get("settings_feedback"),
                    subtitle = null,
                    onClick = { uriHandler.openUri("https://forms.gle/f3bpNuVSxhAGytWy5") },
                    isSelected = false,
                    showDescription = showDescriptions,
                )
            }

            // ── Section 5: Account Actions ──
            item { Spacer(modifier = Modifier.height(24.dp)) }
            if (!isGuestMode) {
                item { HorizontalDivider(thickness = 0.5.dp, color = TovTalkTheme.colors.border) }
                item {
                    DestructiveActionButton(
                        text = StringProvider.get("profile_logout"),
                        onClick = { showLogoutDialog = true }
                    )
                }
                item { HorizontalDivider(thickness = 0.5.dp, color = TovTalkTheme.colors.border) }
                item {
                    DestructiveActionButton(
                        text = StringProvider.get("settings_delete_account"),
                        onClick = { showDeleteAccountDialog = true }
                    )
                }
                item { HorizontalDivider(thickness = 0.5.dp, color = TovTalkTheme.colors.border) }
            } else if (onNavigateToLogin != null) {
                item { HorizontalDivider(thickness = 0.5.dp, color = TovTalkTheme.colors.border) }
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(onClick = onNavigateToLogin)
                            .padding(vertical = 16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = StringProvider.get("nav_login"),
                            style = TovTalkTheme.typography.bodyLarge,
                            color = TovTalkTheme.colors.brand,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
                item { HorizontalDivider(thickness = 0.5.dp, color = TovTalkTheme.colors.border) }
            }
            item { Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xl)) }
        }
    }

    if (hideSelfTopBar) {
        // Used as left panel in split view — no scaffold, render list directly
        listContent(PaddingValues(0.dp))
    } else {
        ScreenScaffold(
            topBar = {
                TovTalkTopBar(
                    title = StringProvider.get("settings_title"),
                    onBack = onNavigateBack,
                )
            },
            constrainContentWidth = false,
        ) { paddingValues ->
            CenteredContentWrapper { listContent(paddingValues) }
        }
    }

}

// ── New flat-style helper composables ──

@Composable
private fun SettingsSectionHeader(title: String) {
    Text(
        text = title,
        style = TovTalkTheme.typography.labelMedium,
        color = TovTalkTheme.colors.textSecondary,
        fontWeight = FontWeight.Medium,
        modifier = Modifier.padding(
            start = TovTalkTheme.spacing.screenPadding,
            end = TovTalkTheme.spacing.screenPadding,
            top = 20.dp,
            bottom = 6.dp
        )
    )
}

@Composable
private fun FlatSettingsItem(
    icon: ImageVector,
    title: String,
    subtitle: String?,
    onClick: () -> Unit,
    isSelected: Boolean = false,
    showDescription: Boolean = false,
) {
    val iconColor = if (isSelected) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary
    val backgroundColor = if (isSelected) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft) else Color.Transparent
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(backgroundColor)
            .clickable(onClick = onClick)
            .padding(horizontal = TovTalkTheme.spacing.screenPadding, vertical = 13.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = iconColor,
            modifier = Modifier.size(22.dp)
        )
        Spacer(modifier = Modifier.width(14.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = TovTalkTheme.typography.bodyLarge,
                color = if (isSelected) TovTalkTheme.colors.brand else TovTalkTheme.colors.textPrimary,
                fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
            )
            if (subtitle != null) {
                Text(
                    text = subtitle,
                    style = TovTalkTheme.typography.bodySmall,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        }
        Icon(
            imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = if (isSelected) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary,
            modifier = Modifier.size(20.dp)
        )
    }
}

@Composable
private fun FlatSettingsToggleItem(
    icon: ImageVector,
    title: String,
    subtitle: String?,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onCheckedChange(!checked) }
            .padding(horizontal = TovTalkTheme.spacing.screenPadding, vertical = 13.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = TovTalkTheme.colors.textTertiary,
            modifier = Modifier.size(22.dp)
        )
        Spacer(modifier = Modifier.width(14.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = TovTalkTheme.typography.bodyLarge,
                color = TovTalkTheme.colors.textPrimary
            )
            if (subtitle != null) {
                Text(
                    text = subtitle,
                    style = TovTalkTheme.typography.bodySmall,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        }
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = TovTalkTheme.colors.brand,
                checkedTrackColor = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.half)
            )
        )
    }
}

@Composable
private fun SettingsDivider() {
    HorizontalDivider(
        modifier = Modifier.padding(start = (TovTalkTheme.spacing.screenPadding + 36.dp)),
        thickness = 0.5.dp,
        color = TovTalkTheme.colors.border
    )
}

@Composable
private fun DestructiveActionButton(
    text: String,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 16.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = text,
            style = TovTalkTheme.typography.bodyLarge,
            color = TovTalkTheme.colors.error,
            fontWeight = FontWeight.Medium
        )
    }
}

// ── Dialog composables (kept as-is) ──

@Composable
private fun KashrutSelectionDialog(
    currentLevel: Int,
    availableLevels: List<KosherLevel>,
    onLevelSelected: (Int) -> Unit,
    onDismiss: () -> Unit,
    lang: String = "en"
) {
    var selectedLevel by remember { mutableStateOf(currentLevel) }
    val levelsToShow = if (availableLevels.isEmpty()) emptyList() else availableLevels.sortedBy { it.order }.map { it.id }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(StringProvider.get("settings_kashrut"), style = TovTalkTheme.typography.titleMedium) },
        text = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                levelsToShow.forEach { level ->
                    val levelData = availableLevels.find { it.id == level }
                    val label = levelData?.resolvedName(lang) ?: StringProvider.get("kashrut_level_$level")
                    val requiresVerification = levelData?.requiresGenderVerification == true
                    KashrutDialogOption(
                        level = level,
                        label = label,
                        requiresVerification = requiresVerification,
                        isSelected = selectedLevel == level,
                        onClick = { selectedLevel = level }
                    )
                    if (level != levelsToShow.lastOrNull()) {
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = { onLevelSelected(selectedLevel) }) {
                Text(StringProvider.get("common_save"))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(StringProvider.get("common_cancel"))
            }
        },
        containerColor = TovTalkTheme.colors.surfaceElevated
    )
}

@Composable
private fun KashrutDialogOption(
    level: Int,
    label: String,
    requiresVerification: Boolean,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val backgroundColor by animateColorAsState(
        targetValue = if (isSelected) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft) else TovTalkTheme.colors.surfaceOverlay,
        label = "kashrutOptionBg"
    )
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(TovTalkTheme.shapes.small)
            .background(backgroundColor)
            .clickable(onClick = onClick)
            .padding(TovTalkTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.weight(1f)
        ) {
            Text(
                text = label,
                style = TovTalkTheme.typography.bodyLarge,
                color = if (isSelected) TovTalkTheme.colors.brand else TovTalkTheme.colors.textPrimary,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
            )
            if (requiresVerification) {
                Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                Icon(
                    imageVector = Icons.Default.VerifiedUser,
                    contentDescription = StringProvider.get("settings_requires_verification"),
                    tint = TovTalkTheme.colors.brand,
                    modifier = Modifier.size(TovTalkTheme.sizes.iconXs)
                )
            }
        }
        if (isSelected) {
            Icon(
                imageVector = Icons.Default.Check,
                contentDescription = null,
                tint = TovTalkTheme.colors.brand,
                modifier = Modifier.size(TovTalkTheme.sizes.iconSm)
            )
        }
    }
}

@Composable
private fun LanguageSelectionDialog(
    currentLanguage: String,
    onLanguageSelected: (String) -> Unit,
    onDismiss: () -> Unit
) {
    var selectedLanguage by remember { mutableStateOf(currentLanguage) }
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(StringProvider.get("settings_language"), style = TovTalkTheme.typography.titleMedium) },
        text = {
            Column {
                LanguageDialogOption(
                    languageCode = LanguageCodes.ENGLISH,
                    languageName = StringProvider.get("language_english"),
                    isSelected = selectedLanguage == LanguageCodes.ENGLISH,
                    onClick = { selectedLanguage = LanguageCodes.ENGLISH }
                )
                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                LanguageDialogOption(
                    languageCode = LanguageCodes.HEBREW,
                    languageName = StringProvider.get("language_hebrew"),
                    isSelected = selectedLanguage == LanguageCodes.HEBREW,
                    onClick = { selectedLanguage = LanguageCodes.HEBREW }
                )
            }
        },
        confirmButton = {
            TextButton(onClick = { onLanguageSelected(selectedLanguage) }) {
                Text(StringProvider.get("common_save"))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(StringProvider.get("common_cancel"))
            }
        },
        containerColor = TovTalkTheme.colors.surfaceElevated
    )
}

@Composable
private fun LanguageDialogOption(
    languageCode: String,
    languageName: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val backgroundColor by animateColorAsState(
        targetValue = if (isSelected) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft) else TovTalkTheme.colors.surfaceOverlay,
        label = "langOptionBg"
    )
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(TovTalkTheme.shapes.small)
            .background(backgroundColor)
            .clickable(onClick = onClick)
            .padding(TovTalkTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = languageName,
            style = TovTalkTheme.typography.bodyLarge,
            color = if (isSelected) TovTalkTheme.colors.brand else TovTalkTheme.colors.textPrimary,
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
        )
        if (isSelected) {
            Icon(
                imageVector = Icons.Default.Check,
                contentDescription = null,
                tint = TovTalkTheme.colors.brand,
                modifier = Modifier.size(TovTalkTheme.sizes.iconSm)
            )
        }
    }
}
